import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Select } from 'antd';
import { useAppSelector } from 'app/store';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import TextEditor from 'shared/components/text-editor/TextEditor';
import Title from 'shared/components/title/Title';
import { FaqStatuses } from 'shared/enums/FAQ';
import { useCreateFaqMutation, useGetFaqCategoryQuery } from 'app/services/edpApiV1/faq';
import { currentTimeToBack } from 'shared/helpers/date';
import { SimpleIcon, SimpleIconName } from 'shared/components/icons/icons';
import { ICreateFAQ, TFaqCategoryPayload } from 'shared/types/faq.types';
import { DEFAILT_CATEGORY } from 'shared/constants/faq';
import { addNotifications } from 'shared/helpers/utils';
import { fixContentEditor } from 'shared/helpers/text-editor';
import Required from 'shared/components/required/Required';
import ModalCancelSubmit from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import style from './index.module.scss';

const CreateFaq = () => {
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const me = useAppSelector(state => state.entities.viewer.me);
  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetchig,
  } = useGetFaqCategoryQuery({});
  const navigate = useNavigate();
  const [createFaqRequest, { isLoading: isCreateFAQLoading }] = useCreateFaqMutation();

  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [categoryId, setCategoryId] = useState<typeof DEFAILT_CATEGORY.id>(0);

  const getCategoryRow = (data: TFaqCategoryPayload, isDefault = false) => {
    return {
      value: isDefault ? DEFAILT_CATEGORY.id : data.id,
      label: (
        <div className={style['category-option']}>
          <SimpleIcon name={data.icon as SimpleIconName} /> <span>{data.name}</span>
        </div>
      ),
    };
  };

  const options = useMemo(() => {
    if (!categories?.data) {
      return;
    }
    return [...categories?.data.map(elem => getCategoryRow(elem)), getCategoryRow(DEFAILT_CATEGORY, true)];
  }, [categories?.data]);

  const breadCrumbs = [
    { name: 'Часто задаваемые вопросы', link: '/faq' },
    { name: 'Добавить вопрос', link: `/faq/create` },
  ];

  const prepareFaqToSave = (): ICreateFAQ => {
    return {
      created: currentTimeToBack(),
      title: title,
      content: fixContentEditor(content),
      status: FaqStatuses.UNPUBLICATED,
      categoryId: categoryId,
    };
  };

  const onTitleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(event.target.value);
  };

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    navigate('/faq');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    setOpenCanelModal(true);
  };

  const navigateAfterSuccess = () => {
    navigate('/faq');
  };

  const createFaq = async () => {
    const newFaq = prepareFaqToSave();
    await addNotifications(
      createFaqRequest(newFaq),
      'Вопрос-ответ создан',
      'Ошибка при создании вопроса-ответа',
      navigateAfterSuccess,
    );
  };

  const onCategoryChange = (data: number) => {
    setCategoryId(data);
  };

  const onEditorValueChange = (val: string) => {
    setContent(val);
  };

  const disabled = useMemo(() => {
    return !categoryId || !title || !content || content === '<p><br></p>';
  }, [categoryId, content, title]);

  return (
    <div className={style.wrap}>
      <div className={style['top-block']}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <Button onClick={cancelModalOpen}>Отмена</Button>
        <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
      </div>
      <Title onTitleChange={onTitleChange} />
      <div className={style.categories}>
        <h3>
          <Required>Категория</Required>
        </h3>
        <Select
          style={{ width: '100%' }}
          allowClear
          onChange={onCategoryChange}
          options={options}
          loading={isCategoriesFetchig || isCategoriesLoading}
        />
      </div>
      <TextEditor onValueChange={onEditorValueChange} title="Текст ответа на вопрос" />
      <div className={style['create-btn']}>
        <Button type="primary" onClick={createFaq} disabled={disabled} loading={isCreateFAQLoading}>
          Создать
        </Button>
      </div>
    </div>
  );
};

export default CreateFaq;
